// ABOUTME: Achievement definitions for gamification system
// ABOUTME: Provides unlock criteria, rewards, and metadata for all achievements

import type { Achievement } from '@/types/content'

/**
 * Achievement definitions with unlock criteria
 * Organized by category: progression, mastery, collection, social, special
 */
export const ACHIEVEMENTS: Achievement[] = [
  // ==========================================
  // PROGRESSION ACHIEVEMENTS
  // ==========================================
  {
    id: 'first-scan',
    title: 'First Steps',
    description: 'Scanned your first wardrobe item',
    category: 'progression',
    icon: 'Camera',
    color: '#3b82f6',
    xpReward: 100,
    rarity: 'common',
    criteria: {
      type: 'items_scanned',
      target: 1,
    },
    hidden: false,
    order: 1,
  },

  {
    id: 'level-2',
    title: 'Color Enthusiast',
    description: 'Reached Level 2',
    category: 'progression',
    icon: 'Palette',
    color: '#ec4899',
    xpReward: 150,
    rarity: 'common',
    criteria: {
      type: 'level_reached',
      target: 2,
    },
    hidden: false,
    order: 2,
  },

  {
    id: 'level-5',
    title: 'Fashion Historian',
    description: 'Reached Level 5 - Fashion History',
    category: 'progression',
    icon: 'BookOpen',
    color: '#f59e0b',
    xpReward: 300,
    rarity: 'rare',
    criteria: {
      type: 'level_reached',
      target: 5,
    },
    hidden: false,
    order: 3,
  },

  {
    id: 'level-10',
    title: 'Master Gentleman',
    description: 'Reached Level 10 - Complete Mastery',
    category: 'progression',
    icon: 'Crown',
    color: '#eab308',
    xpReward: 2000,
    rarity: 'legendary',
    criteria: {
      type: 'level_reached',
      target: 10,
    },
    hidden: false,
    order: 4,
  },

  {
    id: 'xp-1000',
    title: 'Getting Started',
    description: 'Earned 1,000 total XP',
    category: 'progression',
    icon: 'Zap',
    color: '#10b981',
    xpReward: 100,
    rarity: 'common',
    criteria: {
      type: 'xp_earned',
      target: 1000,
    },
    hidden: false,
    order: 5,
  },

  {
    id: 'xp-5000',
    title: 'Dedicated Learner',
    description: 'Earned 5,000 total XP',
    category: 'progression',
    icon: 'TrendingUp',
    color: '#10b981',
    xpReward: 500,
    rarity: 'rare',
    criteria: {
      type: 'xp_earned',
      target: 5000,
    },
    hidden: false,
    order: 6,
  },

  {
    id: 'xp-10000',
    title: 'Style Devotee',
    description: 'Earned 10,000 total XP',
    category: 'progression',
    icon: 'Award',
    color: '#10b981',
    xpReward: 1000,
    rarity: 'epic',
    criteria: {
      type: 'xp_earned',
      target: 10000,
    },
    hidden: false,
    order: 7,
  },

  // ==========================================
  // MASTERY ACHIEVEMENTS
  // ==========================================
  {
    id: 'first-challenge',
    title: 'Challenge Accepted',
    description: 'Completed your first challenge',
    category: 'mastery',
    icon: 'Target',
    color: '#8b5cf6',
    xpReward: 150,
    rarity: 'common',
    criteria: {
      type: 'challenges_completed',
      target: 1,
    },
    hidden: false,
    order: 8,
  },

  {
    id: 'challenge-master',
    title: 'Challenge Master',
    description: 'Completed 10 challenges',
    category: 'mastery',
    icon: 'Trophy',
    color: '#8b5cf6',
    xpReward: 500,
    rarity: 'rare',
    criteria: {
      type: 'challenges_completed',
      target: 10,
    },
    hidden: false,
    order: 9,
  },

  {
    id: 'challenge-legend',
    title: 'Challenge Legend',
    description: 'Completed all Level 1-3 challenges',
    category: 'mastery',
    icon: 'Medal',
    color: '#8b5cf6',
    xpReward: 750,
    rarity: 'epic',
    criteria: {
      type: 'challenges_completed',
      target: 12, // All Level 1-3 challenges
    },
    hidden: false,
    order: 10,
  },

  {
    id: 'first-module',
    title: 'Eager Learner',
    description: 'Completed your first educational module',
    category: 'mastery',
    icon: 'GraduationCap',
    color: '#06b6d4',
    xpReward: 100,
    rarity: 'common',
    criteria: {
      type: 'modules_completed',
      target: 1,
    },
    hidden: false,
    order: 11,
  },

  {
    id: 'module-master',
    title: 'Knowledge Seeker',
    description: 'Completed 10 educational modules',
    category: 'mastery',
    icon: 'BookOpenCheck',
    color: '#06b6d4',
    xpReward: 500,
    rarity: 'rare',
    criteria: {
      type: 'modules_completed',
      target: 10,
    },
    hidden: false,
    order: 12,
  },

  {
    id: 'color-master',
    title: 'Color Master',
    description: 'Completed all color theory modules',
    category: 'mastery',
    icon: 'Rainbow',
    color: '#ec4899',
    xpReward: 300,
    rarity: 'rare',
    criteria: {
      type: 'custom',
      target: 1,
      metadata: { requirement: 'complete_all_color_modules' },
    },
    hidden: false,
    order: 13,
  },

  {
    id: 'quiz-master',
    title: 'Quiz Master',
    description: 'Achieved 100% on 20 quizzes',
    category: 'mastery',
    icon: 'Brain',
    color: '#f59e0b',
    xpReward: 600,
    rarity: 'epic',
    criteria: {
      type: 'quizzes_perfect',
      target: 20,
    },
    hidden: false,
    order: 14,
  },

  // ==========================================
  // COLLECTION ACHIEVEMENTS
  // ==========================================
  {
    id: 'wardrobe-organized',
    title: 'Organized Wardrobe',
    description: 'Scanned 10 wardrobe items',
    category: 'collection',
    icon: 'LayoutGrid',
    color: '#14b8a6',
    xpReward: 200,
    rarity: 'common',
    criteria: {
      type: 'items_scanned',
      target: 10,
    },
    hidden: false,
    order: 15,
  },

  {
    id: 'wardrobe-guru',
    title: 'Wardrobe Guru',
    description: 'Scanned 50+ clothing items',
    category: 'collection',
    icon: 'Package',
    color: '#14b8a6',
    xpReward: 750,
    rarity: 'epic',
    criteria: {
      type: 'items_scanned',
      target: 50,
    },
    hidden: false,
    order: 16,
  },

  {
    id: 'wardrobe-master',
    title: 'Wardrobe Master',
    description: 'Scanned 100+ clothing items',
    category: 'collection',
    icon: 'Archive',
    color: '#14b8a6',
    xpReward: 1500,
    rarity: 'legendary',
    criteria: {
      type: 'items_scanned',
      target: 100,
    },
    hidden: false,
    order: 17,
  },

  {
    id: 'complete-categories',
    title: 'Comprehensive Collection',
    description: 'Have items in all 5 categories',
    category: 'collection',
    icon: 'Grid3x3',
    color: '#14b8a6',
    xpReward: 300,
    rarity: 'rare',
    criteria: {
      type: 'custom',
      target: 5,
      metadata: { requirement: 'all_categories_filled' },
    },
    hidden: false,
    order: 18,
  },

  // ==========================================
  // SOCIAL ACHIEVEMENTS (Future)
  // ==========================================
  {
    id: 'style-streak-7',
    title: 'Style Streak',
    description: 'Completed challenges 7 days in a row',
    category: 'social',
    icon: 'Flame',
    color: '#f97316',
    xpReward: 500,
    rarity: 'rare',
    criteria: {
      type: 'streak_days',
      target: 7,
    },
    hidden: false,
    order: 19,
  },

  {
    id: 'style-streak-30',
    title: 'Unstoppable',
    description: 'Completed challenges 30 days in a row',
    category: 'social',
    icon: 'Rocket',
    color: '#f97316',
    xpReward: 2000,
    rarity: 'legendary',
    criteria: {
      type: 'streak_days',
      target: 30,
    },
    hidden: false,
    order: 20,
  },

  // ==========================================
  // SPECIAL/HIDDEN ACHIEVEMENTS
  // ==========================================
  {
    id: 'early-adopter',
    title: 'Early Adopter',
    description: 'Joined during beta testing phase',
    category: 'special',
    icon: 'Star',
    color: '#eab308',
    xpReward: 1000,
    rarity: 'legendary',
    criteria: {
      type: 'custom',
      target: 1,
      metadata: { requirement: 'beta_signup' },
    },
    hidden: true,
    order: 21,
  },

  {
    id: 'perfectionist',
    title: 'Perfectionist',
    description: 'Completed 50 challenges with perfect scores',
    category: 'special',
    icon: 'CheckCircle2',
    color: '#10b981',
    xpReward: 1500,
    rarity: 'epic',
    criteria: {
      type: 'custom',
      target: 50,
      metadata: { requirement: 'perfect_challenge_completions' },
    },
    hidden: true,
    order: 22,
  },

  {
    id: 'fashion-historian',
    title: 'Fashion Historian',
    description: 'Completed all fashion history modules',
    category: 'special',
    icon: 'Scroll',
    color: '#f59e0b',
    xpReward: 1000,
    rarity: 'epic',
    criteria: {
      type: 'custom',
      target: 1,
      metadata: { requirement: 'complete_all_history_modules' },
    },
    hidden: true,
    order: 23,
  },

  {
    id: 'detail-oriented',
    title: 'Detail Oriented',
    description: 'Completed all accessory challenges',
    category: 'special',
    icon: 'Gem',
    color: '#dc2626',
    xpReward: 800,
    rarity: 'rare',
    criteria: {
      type: 'custom',
      target: 1,
      metadata: { requirement: 'complete_all_accessory_challenges' },
    },
    hidden: true,
    order: 24,
  },
]

/**
 * Get achievement by ID
 */
export function getAchievementById(id: string): Achievement | undefined {
  return ACHIEVEMENTS.find((achievement) => achievement.id === id)
}

/**
 * Get achievements by category
 */
export function getAchievementsByCategory(category: string): Achievement[] {
  return ACHIEVEMENTS.filter((achievement) => achievement.category === category).sort(
    (a, b) => a.order - b.order
  )
}

/**
 * Get visible achievements (not hidden or already unlocked)
 */
export function getVisibleAchievements(unlockedIds: string[]): Achievement[] {
  return ACHIEVEMENTS.filter((achievement) => !achievement.hidden || unlockedIds.includes(achievement.id)).sort(
    (a, b) => a.order - b.order
  )
}

/**
 * Check if achievement is unlocked based on criteria
 */
export function checkAchievementUnlock(
  achievement: Achievement,
  userStats: {
    level: number
    totalXP: number
    challengesCompleted: number
    modulesCompleted: number
    itemsScanned: number
    streakDays: number
    perfectQuizzes: number
    outfitsCreated: number
  }
): boolean {
  const { criteria } = achievement

  switch (criteria.type) {
    case 'level_reached':
      return userStats.level >= criteria.target

    case 'xp_earned':
      return userStats.totalXP >= criteria.target

    case 'challenges_completed':
      return userStats.challengesCompleted >= criteria.target

    case 'modules_completed':
      return userStats.modulesCompleted >= criteria.target

    case 'items_scanned':
      return userStats.itemsScanned >= criteria.target

    case 'streak_days':
      return userStats.streakDays >= criteria.target

    case 'quizzes_perfect':
      return userStats.perfectQuizzes >= criteria.target

    case 'custom':
      // Custom criteria require specific implementation
      return false

    default:
      return false
  }
}

/**
 * Get achievements that should be unlocked
 */
export function getNewlyUnlockedAchievements(
  currentUnlockedIds: string[],
  userStats: {
    level: number
    totalXP: number
    challengesCompleted: number
    modulesCompleted: number
    itemsScanned: number
    streakDays: number
    perfectQuizzes: number
    outfitsCreated: number
  }
): Achievement[] {
  return ACHIEVEMENTS.filter(
    (achievement) =>
      !currentUnlockedIds.includes(achievement.id) && checkAchievementUnlock(achievement, userStats)
  )
}

/**
 * Calculate total XP from achievements
 */
export function getTotalAchievementXP(unlockedIds: string[]): number {
  return ACHIEVEMENTS.filter((a) => unlockedIds.includes(a.id)).reduce((sum, a) => sum + a.xpReward, 0)
}

/**
 * Get achievement completion percentage
 */
export function getAchievementProgress(unlockedIds: string[]): number {
  const total = ACHIEVEMENTS.length
  const unlocked = unlockedIds.length
  return Math.floor((unlocked / total) * 100)
}
